import { cn } from "@/lib/format";

/**
 * One measure over the last hour, drawn small next to the number it belongs to: no axes, no legend —
 * the tile's heading names the series, and the number beside it carries the current value.
 */
export function Sparkline({
  points,
  max,
  label,
  className,
}: {
  points: (number | null)[];
  /** Top of the scale; the largest point when left out. */
  max?: number;
  label: string;
  className?: string;
}) {
  const values = points.filter((p): p is number => p !== null);
  if (values.length < 2) return <div className={cn("h-7", className)} aria-hidden />;
  const top = Math.max(max ?? 0, ...values, 1);
  const width = 100;
  const height = 28;
  const step = width / (points.length - 1);
  let path = "";
  let area = "";
  let started = false;
  points.forEach((value, i) => {
    if (value === null) return;
    const x = i * step;
    const y = height - (value / top) * (height - 2) - 1;
    path += `${started ? "L" : "M"}${x.toFixed(2)} ${y.toFixed(2)}`;
    area += `${started ? "L" : `M${x.toFixed(2)} ${height}L`}${x.toFixed(2)} ${y.toFixed(2)}`;
    started = true;
  });
  const lastX = (points.length - 1) * step;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={cn("h-7 w-full text-signal", className)}
      role="img"
      aria-label={label}
    >
      <path d={`${area}L${lastX.toFixed(2)} ${height}Z`} fill="currentColor" opacity="0.12" />
      <path d={path} fill="none" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/** A quiet proportion bar (memory used of total, disk, download progress). */
export function MeterBar({ fraction, className, tone = "signal" }: { fraction: number; className?: string; tone?: "signal" | "ok" | "danger" }) {
  const width = Math.max(0, Math.min(1, fraction)) * 100;
  return (
    <div className={cn("h-1.5 w-full overflow-hidden rounded-full bg-raised", className)}>
      <div
        className={cn("h-full rounded-full", tone === "signal" && "bg-film", tone === "ok" && "bg-ok", tone === "danger" && "bg-danger")}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}
